import React, { useEffect, useMemo, useState } from 'react';
import { Form as FormAnt, InputNumber, Modal, Typography } from 'antd';
import { logs } from '../../../../../../API/TT_ELD/logs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Text } = Typography;

const formatCountdownVerbose = (ms: number) => {
  if (ms <= 0) return '0 minutes';

  const totalSec = Math.floor(ms / 1000);

  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  }
  parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

  return parts.join(' ');
};

const OpenNewDay = ({
  open,
  setOpen,
  refetch,
  driverId,
  lastOpenNewDayRequestDate,
}: {
  open: boolean;
  setOpen(open: boolean): void;
  refetch(): void;
  driverId: number | string | undefined;
  lastOpenNewDayRequestDate: string | null;
}) => {
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form] = FormAnt.useForm();

  const lastOpenedAtUtc = useMemo(() => {
    if (!lastOpenNewDayRequestDate) return null;

    const d = dayjs.utc(lastOpenNewDayRequestDate);
    return d.isValid() ? d : null;
  }, [lastOpenNewDayRequestDate]);

  const nextAllowedAtUtc = useMemo(() => {
    if (!lastOpenedAtUtc) return null;
    return lastOpenedAtUtc.add(24, 'hour');
  }, [lastOpenedAtUtc]);

  const [nowUtc, setNowUtc] = useState(() => dayjs.utc());

  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNowUtc(dayjs.utc()), 1000);
    return () => clearInterval(t);
  }, [open]);

  const isLocked = useMemo(() => {
    if (!nextAllowedAtUtc) return false;
    return nowUtc.isBefore(nextAllowedAtUtc);
  }, [nextAllowedAtUtc, nowUtc]);

  const remainingMs = useMemo(() => {
    if (!nextAllowedAtUtc) return 0;
    const diff = nextAllowedAtUtc.diff(nowUtc);
    return diff > 0 ? diff : 0;
  }, [nextAllowedAtUtc, nowUtc]);

  const handleCancel = () => setOpen(false);

  const handleOk = async () => {
    if (isLocked) return;

    try {
      const values = await form.validateFields();
      setConfirmLoading(true);

      await logs.openNewDay(values, driverId);
      refetch();
      form.resetFields();
      setOpen(false);
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Open new day"
      okText="Submit"
      cancelText="Cancel"
      onCancel={handleCancel}
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: isLocked }}
      onOk={handleOk}
    >
      {lastOpenedAtUtc ? (
        <div style={{ marginBottom: 12 }}>
          {isLocked ? (
            <>
              <Text>You can open after {formatCountdownVerbose(remainingMs)}.</Text>

              <div style={{ marginTop: 6 }}>
                <Text>Your last opened day is {lastOpenedAtUtc.format('MMM D, YYYY')}.</Text>
              </div>
            </>
          ) : (
            <Text type="success">You can open now.</Text>
          )}
        </div>
      ) : (
        ''
      )}

      <FormAnt
        form={form}
        layout="vertical"
        name="form_in_modal"
        initialValues={{ days: 2 }}
        autoComplete="off"
      >
        <FormAnt.Item
          name="days"
          label="Days"
          rules={[
            { required: true, message: 'Days is required' },
            { type: 'number', min: 1, max: 8, message: 'Please enter a number between 1 and 8' },
          ]}
        >
          <InputNumber placeholder="Enter your Day" style={{ width: '100%' }} disabled={isLocked} />
        </FormAnt.Item>
      </FormAnt>
    </Modal>
  );
};

export default OpenNewDay;
